import { Project, Task, TimeEntry } from "../types/Work";
import { API_BASE_URL } from "../config";
import { getToken } from "../lib/tokenStorage";

const formatDateTimeForApi = (date: Date) => {
  const pad = (value: number) => String(value).padStart(2, "0");

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate(),
  )} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(
    date.getSeconds(),
  )}`;
};

// API helper function
async function apiRequest<T>(
  endpoint: string,
  method: string = "GET",
  body?: any
): Promise<T> {
  const token = getToken();
  const headers: HeadersInit = {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ error: "Request failed" }));
    throw new Error(error.error || `HTTP error! status: ${response.status}`);
  }

  return response.json();
}

class WorkStorage {
  // Projects
  async getProjects(): Promise<Project[]> {
    return apiRequest<Project[]>("/work/projects.php");
  }

  async getProject(id: string): Promise<Project | undefined> {
    try {
      return await apiRequest<Project>(`/work/projects.php/${id}`);
    } catch {
      return undefined;
    }
  }

  async createProject(
    project: Omit<Project, "id" | "createdAt" | "updatedAt">
  ): Promise<Project> {
    return apiRequest<Project>("/work/projects.php", "POST", {
      name: project.name,
      color: project.color,
      description: project.description,
    });
  }

  async updateProject(
    id: string,
    updates: Partial<Project>
  ): Promise<Project | undefined> {
    try {
      return await apiRequest<Project>(
        `/work/projects.php/${id}`,
        "PUT",
        updates
      );
    } catch {
      return undefined;
    }
  }

  async deleteProject(id: string): Promise<boolean> {
    try {
      await apiRequest(`/work/projects.php/${id}`, "DELETE");
      return true;
    } catch {
      return false;
    }
  }

  // Tasks
  async getTasks(): Promise<Task[]> {
    const tasks = await apiRequest<any[]>("/work/tasks.php");
    return tasks.map(this.transformTaskFromAPI);
  }

  async getTask(id: string): Promise<Task | undefined> {
    try {
      const task = await apiRequest<any>(`/work/tasks.php/${id}`);
      return this.transformTaskFromAPI(task);
    } catch {
      return undefined;
    }
  }

  async createTask(
    task: Omit<Task, "id" | "createdAt" | "updatedAt">
  ): Promise<Task> {
    const apiTask = await apiRequest<any>("/work/tasks.php", "POST", {
      name: task.name,
      descriptionHtml: task.descriptionHtml,
      priority: task.priority,
      estimationMinutes: task.estimationMinutes,
      projectId: task.projectId,
      status: task.status,
      order: task.order,
      subtasks: task.subtasks,
    });
    return this.transformTaskFromAPI(apiTask);
  }

  async updateTask(
    id: string,
    updates: Partial<Task>
  ): Promise<Task | undefined> {
    try {
      const apiTask = await apiRequest<any>(`/work/tasks.php/${id}`, "PUT", {
        name: updates.name,
        descriptionHtml: updates.descriptionHtml,
        priority: updates.priority,
        estimationMinutes: updates.estimationMinutes,
        projectId: updates.projectId,
        status: updates.status,
        order: updates.order,
        subtasks: updates.subtasks,
      });
      return this.transformTaskFromAPI(apiTask);
    } catch {
      return undefined;
    }
  }

  async deleteTask(id: string): Promise<boolean> {
    try {
      await apiRequest(`/work/tasks.php/${id}`, "DELETE");
      return true;
    } catch {
      return false;
    }
  }

  // Timer operations
  async startTimer(taskId: string): Promise<Task | undefined> {
    try {
      const apiTask = await apiRequest<any>(
        `/work/tasks.php/${taskId}/start-timer`,
        "POST"
      );
      return this.transformTaskFromAPI(apiTask);
    } catch {
      return undefined;
    }
  }

  async stopTimer(taskId: string): Promise<Task | undefined> {
    try {
      const apiTask = await apiRequest<any>(
        `/work/tasks.php/${taskId}/stop-timer`,
        "POST"
      );
      return this.transformTaskFromAPI(apiTask);
    } catch {
      return undefined;
    }
  }

  // Time Entry operations
  async addTimeEntry(
    taskId: string,
    entry: Omit<TimeEntry, "id">
  ): Promise<Task | undefined> {
    try {
      await apiRequest(`/work/time-entries.php/${taskId}`, "POST", {
        start_at: formatDateTimeForApi(entry.startAt),
        end_at: entry.endAt ? formatDateTimeForApi(entry.endAt) : null,
        duration_minutes: entry.durationMinutes,
        note: entry.note,
      });
      return this.getTask(taskId);
    } catch {
      return undefined;
    }
  }

  async updateTimeEntry(
    taskId: string,
    entryId: string,
    updates: Partial<TimeEntry>
  ): Promise<Task | undefined> {
    try {
      const payload: any = {};

      if (updates.startAt !== undefined) {
        payload.start_at = formatDateTimeForApi(updates.startAt);
      }

      if (updates.endAt !== undefined) {
        payload.end_at = updates.endAt
          ? formatDateTimeForApi(updates.endAt)
          : null;
      }

      if (updates.durationMinutes !== undefined) {
        payload.duration_minutes = updates.durationMinutes;
      }

      if (updates.note !== undefined) {
        payload.note = updates.note;
      }

      await apiRequest(
        `/work/time-entries.php/${taskId}/${entryId}`,
        "PUT",
        payload,
      );
      return this.getTask(taskId);
    } catch {
      return undefined;
    }
  }

  async deleteTimeEntry(
    taskId: string,
    entryId: string
  ): Promise<Task | undefined> {
    try {
      await apiRequest(`/work/time-entries.php/${taskId}/${entryId}`, "DELETE");
      return this.getTask(taskId);
    } catch {
      return undefined;
    }
  }

  // Helper to transform API task response to frontend Task type
  private transformTaskFromAPI(apiTask: any): Task {
    return {
      id: apiTask.id,
      name: apiTask.name,
      descriptionHtml: apiTask.description_html || "",
      createdAt: new Date(apiTask.created_at),
      updatedAt: new Date(apiTask.updated_at),
      completedAt: apiTask.completed_at
        ? new Date(apiTask.completed_at)
        : undefined,
      priority: apiTask.priority,
      estimationMinutes: apiTask.estimation_minutes,
      subtasks: (apiTask.subtasks || []).map((s: any) => ({
        id: s.id,
        title: s.title,
        done: !!s.done,
      })),
      projectId: apiTask.project_id,
      status: apiTask.status,
      order: apiTask.order ?? 0,
      timeEntries: (apiTask.timeEntries || []).map((e: any) => ({
        id: e.id,
        startAt: new Date(e.start_at),
        endAt: e.end_at ? new Date(e.end_at) : undefined,
        durationMinutes: e.duration_minutes,
        note: e.note,
      })),
      isTimerRunning: !!apiTask.is_timer_running,
      currentTimerStart: apiTask.current_timer_start
        ? new Date(apiTask.current_timer_start)
        : undefined,
    };
  }
}

export const workStorage = new WorkStorage();
